<template>
  <div class="project-info" v-bind:style="{ minHeight: height + 'px' }">
    <div class="container">
      <div class="project-info-wrapper" v-if="currentKit">
        <div class="project-title">{{ currentKitTitle }}</div>
        <div class="main-info-wrapper">
          <!-- LEFT COLUMN: Project Details -->
          <div class="info-column">
            <h3 class="main-info-title">Project Details</h3>
            <div class="main-info">
              <KitTimelineV2
                :kit="currentKit"
                :reason="currentKit.reason_on_hold"
              />
              <div class="report-sla" v-if="showReportSla">
                <p class="report-sla-pending">Report Pending</p>
                <p class="report-sla-line" v-if="expectedReportDate">
                  Expected completion: <strong>{{ expectedReportDate }}</strong>
                </p>
                <p class="report-sla-line" v-if="dueInBusinessDays !== null">
                  Due in {{ dueInBusinessDays }} Business Day{{ dueInBusinessDays === 1 ? '' : 's' }}
                </p>
              </div>
              <h3 class="kitId">Kit ID: {{ convertKitId(currentKit.kit_id) }}</h3>

              <p class="main-info-subtitle">Sampling Date:</p>
              <p class="main-info-description">{{ formatDate(currentKit.sampling_date) }}</p>

              <template v-if="hasAddress">
                <p class="main-info-subtitle">Sampling Location:</p>
                <p class="main-info-description">{{ samplingLocation }}</p>
              </template>

              <template v-if="collectedBy">
                <p class="main-info-subtitle">Samples Collected By:</p>
                <p class="main-info-description">{{ collectedBy }}</p>
              </template>
            </div>
            <div v-if="!mobile" style="width:100%" id="btns-complate-wrap-dl">
              <a target="_blank" v-if="isCompleted" class="btn-link">
                <button @click.prevent="getFile" class="download-btn">
                  Download Report
                </button>
                <span class="loading-text" v-if="loadingPdf">Loading...</span>
              </a>
            </div>
          </div>

          <!-- RIGHT COLUMN: Dust Sample Results + Share Report -->
          <div class="info-column" id="btns-complate-wrap">
            <h3 class="main-info-title">Dust Sample Results</h3>
            <div
              class="main-info"
              v-for="(item, key) in indoorSamples"
              :key="'sample-' + key"
            >
              <p class="main-info-subtitle">Indoor Sample #{{ key + 1 }}</p>
              <p class="main-info-description">{{ item.type || item.label }}</p>
              <p
                class="main-info-status unable_to_process"
                v-if="item.result === -1"
              >
                {{ formatResultLabel(item.result_label) || 'Unable To Process' }}
              </p>
              <p
                class="main-info-status not_evident"
                v-else-if="item.result === 0"
              >
                {{ formatResultLabel(item.result_label) || 'Normal Fungal Ecology' }}
              </p>
              <p
                class="main-info-status slightly_evident"
                v-else-if="item.result === 1"
              >
                {{ formatResultLabel(item.result_label) || 'Slightly Disrupted' }}
              </p>
              <p
                class="main-info-status moderately_evident"
                v-else-if="item.result === 2"
              >
                {{ formatResultLabel(item.result_label) || 'Moderately Disrupted' }}
              </p>
              <p
                class="main-info-status significantly_evident"
                v-else-if="item.result === 3"
              >
                {{ formatResultLabel(item.result_label) || 'Significantly Disrupted' }}
              </p>
              <p class="main-info-status pending" v-else>Results Pending</p>
            </div>
            <div class="main-info-email form">
              <div class="form">
                <p class="main-info-subtitle text-center">Share Report?</p>
                <p class="main-info-subtitle mt-10 text-center my-10">
                  <input
                    type="text"
                    class="input"
                    name="report_name"
                    placeholder="Name"
                    v-model="reportName"
                  />
                </p>
                <p class="main-info-subtitle mt-10 text-center my-10">
                  <input
                    type="text"
                    class="input toLowerCase"
                    :class="{ invalid: emailError }"
                    name="report_forward_email"
                    placeholder="Email Address"
                    v-model="reportEmail"
                    @input="emailError = ''"
                  />
                  <span class="validate-error" v-if="emailError">{{ emailError }}</span>
                </p>
                <p class="main-info-subtitle mt-10 text-center">
                  <textarea
                    name="report_comment"
                    placeholder="Comment"
                    id="comment"
                    v-model="reportComment"
                  ></textarea>
                </p>
                <div class="text-center mt-10 pt-10">
                  <button class="btn" @click.prevent="shareReport">
                    <span v-if="currentKit.report">Send</span>
                    <span v-else>Save</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="mobile" id="btns-complate-wrap-dl-mobile">
          <a target="_blank" v-if="isCompleted" class="btn-link dwn">
            <button @click.prevent="getFile" class="download-btn">
              Download Report
            </button>
            <span class="loading-text" v-if="loadingPdf">Loading...</span>
          </a>
        </div>
      </div>
    </div>

    <ActivateNewKit
      v-if="showActivate"
      :newKitModal="newKitModal"
      @resetModal="val => showActivate = val"
      @changeHeight="h => height = h + 136"
    />
    <CommonAlertModal
      v-if="alertModal"
      @closeAlertModal="val => alertModal = val"
      :message="alertMessage"
      :emails="alertEmails"
    />
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters, mapActions } from "vuex";
import KitTimelineV2 from "../components/KitTimelineV2";
import ActivateNewKit from "../../components/home/ActivateNewKit";
import CommonAlertModal from "../../components/CommonAlertModal";

const DASHES_REGEX = /[-\s]/g;

export default {
  name: "V2ProjectInfo",
  components: { KitTimelineV2, ActivateNewKit, CommonAlertModal },
  props: ["newKitModal"],
  data() {
    return {
      currentKit: null,
      currentKitTitle: null,
      showActivate: false,
      height: 685,
      mobile: false,
      loadingPdf: false,
      reportEmail: null,
      reportComment: null,
      reportName: "",
      alertModal: false,
      alertMessage: "",
      alertEmails: "",
      emailError: "",
      files_url: process.env.VUE_APP_FILES_URL,
      api_url: process.env.VUE_APP_API_URL
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    hasAddress() {
      return this.currentKit && this.currentKit.address && this.currentKit.address.address;
    },
    samplingLocation() {
      if (!this.hasAddress) return "";
      const a = this.currentKit.address;
      return `${a.address}, ${a.city}, ${a.state} ${a.zip}`;
    },
    collectedBy() {
      if (!this.currentKit) return "";
      const { collected_first_name, collected_last_name } = this.currentKit;
      if (!collected_first_name && !collected_last_name) return "";
      return `${collected_first_name || ""} ${collected_last_name || ""}`.trim();
    },
    kitStatusString() {
      if (!this.currentKit) return "";
      const s = this.currentKit.status;
      if (typeof s === "string") return s;
      const map = {
        0: "shipped", 1: "activated", 2: "lab_awaiting", 3: "lab_received",
        4: "on_hold", 5: "analysis_completed", 6: "problem", 7: "delivered",
        8: "archive", 9: "returned", 10: "analysis_failed", 11: "partially_failed", 12: "in_active",
        13: "dna_extraction", 14: "sequencing", 15: "report_ready", 16: "accepted"
      };
      return map[s] || "shipped";
    },
    indoorSamples() {
      if (!this.currentKit) return [];
      // V2 kits may store samples as 'samples' or 'indoorSamples'
      return this.currentKit.indoorSamples || this.currentKit.samples || [];
    },
    isCompleted() {
      return this.kitStatusString === "analysis_completed";
    },
    // Report SLA panel shows while the business status is Lab Received — the
    // report is pending throughout the lab's internal processing (the lab's
    // internal stages don't change the business status).
    showReportSla() {
      return this.kitStatusString === "lab_received";
    },
    expectedReportDate() {
      if (!this.currentKit || !this.currentKit.expected_report_at) return null;
      const d = new Date(this.currentKit.expected_report_at);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    },
    dueInBusinessDays() {
      if (!this.currentKit || !this.currentKit.expected_report_at) return null;
      const target = new Date(this.currentKit.expected_report_at);
      if (isNaN(target.getTime())) return null;
      const today = new Date();
      target.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      if (target <= today) return 0;
      let count = 0;
      const cursor = new Date(today);
      while (cursor < target) {
        cursor.setDate(cursor.getDate() + 1);
        const dow = cursor.getDay();
        if (dow !== 0 && dow !== 6) count += 1;
      }
      return count;
    }
  },
  methods: {
    ...mapActions(["profile", "shareReportV2"]),
    handleResize() {
      this.mobile = window.innerWidth < 769;
    },
    parseKitId(kitId) {
      return kitId ? kitId.replace(DASHES_REGEX, "").substr(0, 11).toUpperCase() : undefined;
    },
    convertKitId(kitId) {
      const raw = this.parseKitId(kitId);
      return raw
        ? `${raw.substr(0, 2)}-${raw.substr(2, 2)}-${raw.substr(4, 3)}-${raw.substr(7, 4)}`
        : undefined;
    },
    getSampleStatus(sampleIndex) {
      if (!this.currentKit || !this.currentKit.report || !this.currentKit.report.reportSamples) return null;
      const sample = this.currentKit.report.reportSamples[sampleIndex];
      return sample ? sample.status : null;
    },
    async getFile() {
      if (!this.currentKit || !this.currentKit.id || this.loadingPdf) return;
      this.loadingPdf = true;
      try {
        const response = await axios({
          url: `${this.api_url}/v2/testkits/${this.currentKit.id}/report/pdf`,
          method: "GET",
          responseType: "blob"
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `KIT-ID-${this.convertKitId(this.currentKit.kit_id)}.pdf`
        );
        const anchor =
          document.getElementById(
            this.mobile ? "btns-complate-wrap-dl-mobile" : "btns-complate-wrap-dl"
          ) || document.body;
        anchor.appendChild(link);
        link.click();
        anchor.removeChild(link);
        window.URL.revokeObjectURL(url);
      } finally {
        this.loadingPdf = false;
      }
    },
    setCurrentKit() {
      const { id } = this.$route.params;
      const kits = this.currentUser.kits_v2 || [];
      kits.forEach((kit, index) => {
        if (kit.kit_id === id) {
          this.currentKit = kit;
          this.currentKitTitle = kit.title || `Project ${index + 1}`;
        }
      });
    },
    formatDate(date) {
      if (!date) return "00/00/0000";
      const samplingDate = new Date(date);
      if (isNaN(samplingDate.getTime())) return "00/00/0000";
      const options = { month: "long", day: "numeric", year: "numeric" };
      return samplingDate.toLocaleDateString("en-US", options);
    },
    formatResultLabel(label) {
      if (!label) return "";
      return String(label)
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    },
    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    async shareReport() {
      if (this.reportEmail && !this.isValidEmail(this.reportEmail)) {
        this.emailError = "Please enter a valid email address";
        return;
      }
      this.emailError = "";
      await this.shareReportV2({
        report_forward_email: this.reportEmail || null,
        report_comment: this.reportComment || null,
        report_name: this.reportName || null,
        kit_id: this.currentKit.kit_id
      });
      if (this.currentKit.report) {
        this.alertMessage = "Report Shared";
        this.alertEmails = this.reportEmail;
      } else {
        this.alertMessage = "Email Address Saved";
        this.alertEmails = this.reportEmail;
      }
      this.alertModal = true;
    }
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
    if (!this.currentUser || !this.currentUser.kits_v2) {
      await this.profile();
    }
    this.setCurrentKit();
    if (this.currentKit !== null) {
      if (this.currentKit.report_forward_email !== null) {
        this.reportEmail = this.currentKit.report_forward_email;
      }
      if (this.currentKit.report_comment !== null) {
        this.reportComment = this.currentKit.report_comment;
      }
      if (this.currentKit.report_name !== null) {
        this.reportName = this.currentKit.report_name;
      }
    }
  },
  watch: {
    newKitModal() {
      this.showActivate = true;
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../styles/colors";

#comment {
  height: 110px;
  padding: 5px 20px;
  color: $gray;
  width: 70%;
  font-size: 16px;
  outline: none;
  border-radius: 10px;
  border: 1px solid #cccccc;
  font-family: TTNormsMedium, sans-serif;
}
.dwn {
  padding-top: 5px;
}
.project-info {
  background-color: #f4f8f9;
  width: 100%;
  position: relative;
  min-height: calc(100vh - 60px);
}
.btn {
  background-color: $green;
  width: 30%;
  justify-content: center;
  align-items: center;
  color: $white;
  cursor: pointer;
  outline: none;
  border: 1px solid transparent;
  height: 36px;
  border-radius: 25px;
  transition: all 0.5s linear;
  font-size: 18px;
  font-family: TTNormsBold, sans-serif;
}
.text-center {
  text-align: center;
}
.mt-10 {
  margin-top: 10px;
}
.container {
  .project-info-wrapper {
    width: 100%;
    min-height: calc(100vh - 60px);
    padding-top: 125px;
    font-family: TTNormsRegular, sans-serif;
    position: relative;
    display: flex;
    flex-direction: column;
    .project-title {
      width: 100%;
      height: 60px;
      background-color: $blue;
      border-radius: 15px;
      display: flex;
      justify-content: center;
      align-items: center;
      color: $white;
      font-size: 28px;
    }
    .main-info-wrapper {
      display: flex;
      justify-content: space-between;
      .info-column {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 48%;
        margin-top: 25px;
        .main-info-title {
          text-transform: uppercase;
          color: $blue;
          font-size: 16px;
          font-family: TTNormsMedium, sans-serif;
        }
        .main-info {
          width: 100%;
          padding: 30px 40px;
          display: flex;
          flex-direction: column;
          background-color: $white;
          border-radius: 15px;
          box-shadow: 0 3px 15px -9px $boxShadow;
          color: $gray;
          margin: 20px 0 0 0;
          .kitId {
            color: $blue;
            text-transform: uppercase;
            font-size: 16px;
            font-family: TTNormsMedium, sans-serif;
            margin-bottom: 20px;
          }
          .report-sla {
            margin: 4px 0 16px 0;
            padding: 10px 14px;
            background: #f0f7fb;
            border-radius: 10px;
            .report-sla-pending {
              font-family: TTNormsMedium, sans-serif;
              color: #b45309;
              margin-bottom: 4px;
            }
            .report-sla-line {
              font-family: TTNormsRegular, sans-serif;
              color: $gray;
              font-size: 14px;
              margin-bottom: 2px;
            }
          }
          .main-info-subtitle {
            font-family: TTNormsMedium, sans-serif;
            margin-bottom: 5px;
            color: $gray;
          }
          .main-info-description {
            margin-bottom: 16px;
            font-family: TTNormsRegular, sans-serif;
            color: $gray;
            &:last-child {
              margin-bottom: 0;
            }
          }
          .main-info-status {
            margin-top: 4px;
            &.pending {
              font-family: TTNormsMedium, sans-serif;
              position: relative;
              &:after {
                content: "";
                display: block;
                position: absolute;
                width: 100px;
                height: 60px;
                top: -55px;
                right: 0;
                background: url("../../images/dashboard/no_results.png") center
                  no-repeat;
                background-size: 100px;
              }
            }
            &.unable_to_process {
              font-family: TTNormsMedium, sans-serif;
              color: $gray;
              position: relative;
              &:after {
                content: "";
                display: block;
                position: absolute;
                width: 100px;
                height: 60px;
                top: -55px;
                right: 0;
                background: url("../../images/dashboard/warning_gauge.svg") center
                  no-repeat;
                background-size: 100px;
              }
            }
            &.not_evident {
              font-family: TTNormsMedium, sans-serif;
              color: $green;
              position: relative;
              &:after {
                content: "";
                display: block;
                position: absolute;
                width: 100px;
                height: 60px;
                top: -55px;
                right: 0;
                background: url("../../images/dashboard/no-evident.png") center
                  no-repeat;
                background-size: 100px;
              }
            }
            &.slightly_evident {
              font-family: TTNormsMedium, sans-serif;
              color: $yellow;
              position: relative;
              &:after {
                content: "";
                display: block;
                position: absolute;
                width: 100px;
                height: 60px;
                top: -55px;
                right: 0;
                background: url("../../images/dashboard/slightly_evident.png")
                  center no-repeat;
                background-size: 100px;
              }
            }
            &.moderately_evident {
              font-family: TTNormsMedium, sans-serif;
              color: orange;
              position: relative;
              &:after {
                content: "";
                display: block;
                position: absolute;
                width: 100px;
                height: 60px;
                top: -55px;
                right: 0;
                background: url("../../images/dashboard/moderate_evident.png")
                  center no-repeat;
                background-size: 100px;
              }
            }
            &.significantly_evident {
              font-family: TTNormsMedium, sans-serif;
              color: $red;
              position: relative;
              &:after {
                content: "";
                display: block;
                position: absolute;
                width: 100px;
                height: 60px;
                top: -55px;
                right: 0;
                background: url("../../images/dashboard/significantly_evident.png")
                  center no-repeat;
                background-size: 100px;
              }
            }
          }
        }
      }
    }
    .btn-link {
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
      align-self: flex-end;
      .download-btn {
        width: 180px;
        height: 45px;
        border-radius: 25px;
        background-color: $blue;
        color: $white;
        border: none;
        outline: none;
        font-family: TTNormsMedium, sans-serif;
        font-size: 16px;
        cursor: pointer;
        margin: 20px 0 5px 0;
      }
    }
  }
}
@media screen and (max-width: 768px) {
  .container {
    .project-info-wrapper {
      padding-top: 90px;
      .main-info-wrapper {
        flex-direction: column;
        .info-column {
          width: 100%;
          .main-info {
            padding: 20px;
          }
        }
      }
      .btn-link {
        align-self: center;
      }
    }
  }
}
.input {
  padding: 5px 20px;
  color: $gray;
  width: 70%;
  font-size: 16px;
  text-align: center;
  outline: none;
  border-radius: 10px;
  border: 1px solid #cccccc;
  font-family: TTNormsMedium, sans-serif;
}
.invalid {
    border: 1px solid $red;
}
.validate-error {
  display: block;
  color: $red;
  font-size: 12px;
  margin-top: 4px;
  font-family: TTNormsMedium, sans-serif;
}
.main-info-email {
  width: 100%;
  padding: 20px 40px;
  display: flex;
  flex-direction: column;
  background-color: $white;
  border-radius: 15px;
  box-shadow: 0 3px 15px -9px $boxShadow;
  color: $gray;
  margin: 20px 0 0 0;
  .main-info-subtitle {
    font-family: TTNormsMedium, sans-serif;
    margin-bottom: 5px;
    color: $gray;
  }
}
.my-10 {
  padding: 15px 0px;
}
.pt-10 {
  padding-bottom: 10px;
}
</style>
